import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { FileText, RefreshCw, ChevronDown, ChevronUp, Search } from "lucide-react";

const API_URL = "/api";

const STATUS_CONFIG = {
  draft:       { label: "Draft",      color: "bg-gray-100 text-gray-700 border-gray-200" },
  submitted:   { label: "Dikirim",    color: "bg-blue-100 text-blue-700 border-blue-200" },
  received:    { label: "Diterima",   color: "bg-cyan-100 text-cyan-700 border-cyan-200" },
  in_progress: { label: "Diproses",   color: "bg-yellow-100 text-yellow-700 border-yellow-200" },
  completed:   { label: "Selesai",    color: "bg-green-100 text-green-700 border-green-200" },
  rejected:    { label: "Ditolak",    color: "bg-red-100 text-red-700 border-red-200" },
};

const NEXT_STATUS = {
  submitted: [{ to: "received",  label: "Terima", color: "bg-cyan-500 hover:bg-cyan-600" },
              { to: "rejected",  label: "Tolak",  color: "bg-red-500 hover:bg-red-600" }],
  received:  [{ to: "completed", label: "Selesaikan", color: "bg-green-500 hover:bg-green-600" },
              { to: "rejected",  label: "Tolak",  color: "bg-red-500 hover:bg-red-600" }],
};

// Actions hanya tampil di tab antrian
const ANTRIAN_ONLY_ACTIONS = true;

const fmt = (n) => n ? Number(n).toLocaleString("id-ID") : "0";
const getToken = () => localStorage.getItem("token");
const getUser  = () => { try { return JSON.parse(localStorage.getItem("user") || "{}"); } catch { return {}; } };

// ── Sub-komponen List Submission ─────────────────────────────
const SubmissionList = ({ submissions, isLoading, detailCache, setDetailCache, expandedId, setExpandedId, onUpdateStatus, approvalNote, setApprovalNote, isUpdating, showActions = true, selectedIds = [], setSelectedIds = () => {} }) => {
  const fetchDetail = async (id) => {
    if (detailCache[id]) return;
    try {
      const res = await axios.get(`${API_URL}/duty-tax/${id}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      setDetailCache(prev => ({ ...prev, [id]: res.data }));
    } catch { toast.error("Gagal memuat detail"); }
  };

  const toggleExpand = async (id) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    await fetchDetail(id);
  };

  if (isLoading) return <div className="text-center py-12 text-gray-400">Memuat data...</div>;
  if (!submissions.length) return <div className="text-center py-12 text-gray-400 bg-white rounded-2xl border border-gray-100">Tidak ada data.</div>;

  return (
    <div className="space-y-3">
      {submissions.map(s => {
        const cfg    = STATUS_CONFIG[s.status] || {};
        const isOpen = expandedId === s.id;
        const detail = detailCache[s.id];
        const actions = NEXT_STATUS[s.status] || [];
        const isExport = s.type === "export";
        const cols = isExport
          ? [["handling","Handling"],["sewa_gudang","Sewa Gudang"],["barcode","Barcode"],["cargo_sec","Cargo Sec"],["messaging_fee","Msg Fee"],["neutral_awb","Neutral AWB"],["reg_agent","Reg. Agent"],["forklip","Forklip"],["adm","ADM"]]
          : [["handling","Handling"],["sewa_gudang","Sewa Gudang"],["additional_1","Add. 1"],["additional_2","Add. 2"]];

        return (
          <div key={s.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="flex items-center justify-between p-4 hover:bg-gray-50">
              <div className="flex items-center gap-4">
                {!showActions && (
                  <input type="checkbox"
                    checked={selectedIds.includes(s.id)}
                    onChange={e => {
                      e.stopPropagation();
                      setSelectedIds(prev => e.target.checked ? [...prev, s.id] : prev.filter(i => i !== s.id));
                    }}
                    onClick={e => e.stopPropagation()}
                    className="w-4 h-4 rounded border-gray-300 cursor-pointer flex-shrink-0"
                  />
                )}
                <div className="cursor-pointer flex items-center gap-4" onClick={() => toggleExpand(s.id)}>
                  <div>
                    <div className="font-semibold text-gray-900">{s.submission_no}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {s.type.toUpperCase()} · {s.template === "rekap_warehouse_cost" ? `Rekap · ${s.gudang || ""}` : "Warehouse Cost"} · {s.submission_date} · oleh {s.submitted_by}
                    </div>
                  </div>
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${cfg.color}`}>{cfg.label}</span>
                  {s.invoice_no
                    ? <span className="text-xs bg-green-100 text-green-700 px-2.5 py-1 rounded-full border border-green-200 font-medium">→ {s.invoice_no}</span>
                    : (s.status === "received" || s.status === "completed") && <span className="text-xs bg-gray-100 text-gray-500 px-2.5 py-1 rounded-full border border-gray-200">Uninvoiced</span>
                  }
                </div>
              </div>
              <div className="flex items-center gap-3">
                {s.notes_finance && <span className="text-xs text-blue-600 max-w-xs truncate hidden md:block">{s.notes_finance}</span>}
                {isOpen ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
              </div>
            </div>

            {isOpen && (
              <div className="border-t border-gray-100 p-4 space-y-4">
                {!detail ? <div className="text-center text-gray-400 py-4">Memuat detail...</div> : (
                  <>
                    <div className="overflow-x-auto">
                      {detail.submission.template === "rekap_warehouse_cost" ? (
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-gray-50 text-gray-600 text-xs">
                              <th className="px-3 py-2 text-left">#</th>
                              <th className="px-3 py-2 text-left">MAWB</th>
                              {detail.submission.type === "import" ? <th className="px-3 py-2 text-left">HAWB</th> : <th className="px-3 py-2 text-right">Deposit</th>}
                              <th className="px-3 py-2 text-left">No. OR</th>
                              <th className="px-3 py-2 text-right font-bold">Jumlah</th>
                              <th className="px-3 py-2 text-left">Keterangan</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detail.items.map((item, i) => (
                              <tr key={item.id} className="border-t border-gray-50">
                                <td className="px-3 py-2 text-gray-400">{i+1}</td>
                                <td className="px-3 py-2 font-medium">{item.cwb_awb_cnee}</td>
                                {detail.submission.type === "import" ? <td className="px-3 py-2">{item.hawb || "-"}</td> : <td className="px-3 py-2 text-right">{fmt(item.deposit)}</td>}
                                <td className="px-3 py-2">{item.no_or || "-"}</td>
                                <td className="px-3 py-2 text-right font-bold text-blue-700">{fmt(item.jumlah)}</td>
                                <td className="px-3 py-2 text-gray-500">{item.keterangan || "-"}</td>
                              </tr>
                            ))}
                            <tr className="border-t-2 border-gray-200 bg-gray-50 font-bold text-sm">
                              <td colSpan={detail.submission.type === "import" ? 4 : 4} className="px-3 py-2 text-right text-gray-600">TOTAL</td>
                              <td className="px-3 py-2 text-right text-blue-700">{fmt(detail.items.reduce((s,i) => s+Number(i.jumlah),0))}</td>
                              <td></td>
                            </tr>
                          </tbody>
                        </table>
                      ) : (
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-gray-50 text-gray-600 text-xs">
                              <th className="px-3 py-2 text-left">#</th>
                              <th className="px-3 py-2 text-left">{isExport ? "No. AWB" : "CWB / AWB / CNEE"}</th>
                              <th className="px-3 py-2 text-right">Berat</th>
                              <th className="px-3 py-2 text-left">Tanggal</th>
                              {cols.map(([k,l]) => <th key={k} className="px-3 py-2 text-right">{l}</th>)}
                              <th className="px-3 py-2 text-right font-bold">Jumlah</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detail.items.map((item, i) => (
                              <tr key={item.id} className="border-t border-gray-50">
                                <td className="px-3 py-2 text-gray-400">{i+1}</td>
                                <td className="px-3 py-2 font-medium">{item.cwb_awb_cnee}</td>
                                <td className="px-3 py-2 text-right text-gray-600">{item.weight ? `${item.weight} kg` : "-"}</td>
                                <td className="px-3 py-2 text-gray-600">{item.item_date}</td>
                                {cols.map(([k]) => <td key={k} className="px-3 py-2 text-right">{fmt(item[k])}</td>)}
                                <td className="px-3 py-2 text-right font-bold text-blue-700">{fmt(item.jumlah)}</td>
                              </tr>
                            ))}
                            <tr className="border-t-2 border-gray-200 bg-gray-50 font-bold text-sm">
                              <td colSpan={4} className="px-3 py-2 text-right text-gray-600">TOTAL</td>
                              {cols.map(([k]) => <td key={k} className="px-3 py-2 text-right">{fmt(detail.items.reduce((s,i) => s+Number(i[k]||0),0))}</td>)}
                              <td className="px-3 py-2 text-right text-blue-700">{fmt(detail.items.reduce((s,i) => s+Number(i.jumlah),0))}</td>
                            </tr>
                          </tbody>
                        </table>
                      )}
                    </div>

                    {detail.submission.notes_clearance && (
                      <div className="text-sm bg-gray-50 rounded-xl p-3">
                        <span className="font-medium text-gray-600">Catatan Clearance: </span>{detail.submission.notes_clearance}
                      </div>
                    )}

                    {detail.logs.length > 0 && (
                      <div className="space-y-1">
                        <div className="text-xs font-medium text-gray-500 mb-2">Riwayat Status</div>
                        {detail.logs.map(log => (
                          <div key={log.id} className="flex items-center gap-2 text-xs text-gray-500">
                            <span className={`px-2 py-0.5 rounded-full border text-xs ${STATUS_CONFIG[log.new_status]?.color}`}>{STATUS_CONFIG[log.new_status]?.label}</span>
                            <span>{log.created_at?.replace("T"," ").slice(0,16)} · {log.changed_by} · {log.source_system}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {actions.length > 0 && showActions && (
                      <div className="border-t border-gray-100 pt-4 space-y-3">
                        <textarea value={approvalNote[s.id] || ""} onChange={e => setApprovalNote(prev => ({...prev, [s.id]: e.target.value}))}
                          placeholder="Catatan untuk Tim Clearance (opsional)..." className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm resize-none" rows={2} />
                        <div className="flex gap-2 justify-end">
                          {actions.map(action => (
                            <button key={action.to} onClick={() => onUpdateStatus(s.id, action.to, s.status)} disabled={isUpdating}
                              className={`flex items-center gap-2 px-4 py-2 text-sm text-white rounded-xl transition-colors disabled:opacity-50 ${action.color}`}>
                              {action.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ── Komponen Filter + Export ─────────────────────────────────
const FilterExport = ({ filters, setFilters, onSearch, extraFilters, onExport, selectedIds, onSelectAll, onClearAll, submissions }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-3">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {extraFilters}
      <input type="date" value={filters.date_from} onChange={e => setFilters(p => ({...p, date_from: e.target.value}))}
        className="border border-gray-200 rounded-xl px-3 py-2 text-sm" />
      <input type="date" value={filters.date_to} onChange={e => setFilters(p => ({...p, date_to: e.target.value}))}
        className="border border-gray-200 rounded-xl px-3 py-2 text-sm" />
    </div>
    <div className="flex items-center gap-2 flex-wrap">
      <button onClick={onSearch} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-xl hover:bg-blue-700">
        <Search size={14} /> Cari
      </button>
      <div className="h-6 w-px bg-gray-200 mx-1" />
      <button onClick={onSelectAll} className="px-3 py-2 text-xs border border-gray-200 rounded-xl hover:bg-gray-50 text-gray-600">
        Pilih Semua
      </button>
      <button onClick={onClearAll} className="px-3 py-2 text-xs border border-gray-200 rounded-xl hover:bg-gray-50 text-gray-600">
        Batal Semua
      </button>
      {selectedIds.length > 0 && (
        <span className="text-xs bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full font-medium">
          {selectedIds.length} dipilih
        </span>
      )}
      <div className="h-6 w-px bg-gray-200 mx-1" />
      <button onClick={() => onExport("excel")} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm rounded-xl hover:bg-green-700">
        📊 {selectedIds.length > 0 ? `Excel (${selectedIds.length})` : "Excel (Semua)"}
      </button>
      <button onClick={() => onExport("pdf")} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm rounded-xl hover:bg-red-700">
        📄 {selectedIds.length > 0 ? `PDF (${selectedIds.length})` : "PDF (Semua)"}
      </button>
    </div>
  </div>
);

// ── Main Component ────────────────────────────────────────────
const DutyTaxPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const user = getUser();
    if (user.role === "ADMIN") return;
    let perms = [];
    try { perms = JSON.parse(user.permissions || "[]"); } catch {}
    if (!perms.includes("Modul Report Clearance")) navigate("/portal");
  }, [navigate]);

  const [activeTab, setActiveTab] = useState("antrian");

  // Shared state
  const [submissions, setSubmissions] = useState([]);
  const [isLoading, setIsLoading]     = useState(false);
  const [expandedId, setExpandedId]   = useState(null);
  const [detailCache, setDetailCache] = useState({});
  const [approvalNote, setApprovalNote] = useState({});
  const [isUpdating, setIsUpdating]   = useState(false);
  const [page, setPage]               = useState(1);
  const [total, setTotal]             = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const limit = 20;

  // Filters per tab
  const [wcFilters, setWcFilters] = useState({ type: "import", date_from: "", date_to: "" });
  const [rekapFilters, setRekapFilters] = useState({ type: "import", gudang: "MSA", date_from: "", date_to: "" });

  const fetchSubmissions = useCallback(async (pg = 1, tab = activeTab) => {
    setIsLoading(true);
    try {
      let params = { page: pg, limit };
      if (tab === "antrian") {
        params.status = "submitted";
      } else if (tab === "warehouse_cost") {
        params.template = "warehouse_cost";
        params.type = wcFilters.type;
        if (wcFilters.date_from) params.date_from = wcFilters.date_from;
        if (wcFilters.date_to)   params.date_to   = wcFilters.date_to;
      } else if (tab === "rekap") {
        params.template = "rekap_warehouse_cost";
        params.type = rekapFilters.type;
        params.gudang = rekapFilters.gudang;
        if (rekapFilters.date_from) params.date_from = rekapFilters.date_from;
        if (rekapFilters.date_to)   params.date_to   = rekapFilters.date_to;
      }
      const res = await axios.get(`${API_URL}/duty-tax/`, { headers: { Authorization: `Bearer ${getToken()}` }, params });
      setSubmissions(res.data.data);
      setTotal(res.data.total);
      setPage(pg);
    } catch { toast.error("Gagal memuat data"); }
    finally { setIsLoading(false); }
  }, [activeTab, wcFilters, rekapFilters]);

  useEffect(() => { fetchSubmissions(1, activeTab); }, [activeTab]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setExpandedId(null);
    setSubmissions([]);
    setSelectedIds([]);
    // Reset filter ke default
    setWcFilters({ type: "import", date_from: "", date_to: "" });
    setRekapFilters({ type: "import", gudang: "MSA", date_from: "", date_to: "" });
  };

  const exportReport = (format, tab) => {
    const token = getToken();
    const params = new URLSearchParams();
    if (tab === "warehouse_cost") {
      params.append("template", "warehouse_cost");
      params.append("type", wcFilters.type);
      if (wcFilters.date_from) params.append("date_from", wcFilters.date_from);
      if (wcFilters.date_to)   params.append("date_to",   wcFilters.date_to);
    } else {
      params.append("template", "rekap_warehouse_cost");
      params.append("type", rekapFilters.type);
      params.append("gudang", rekapFilters.gudang);
      if (rekapFilters.date_from) params.append("date_from", rekapFilters.date_from);
      if (rekapFilters.date_to)   params.append("date_to",   rekapFilters.date_to);
    }
    if (selectedIds.length > 0) params.append("ids", selectedIds.join(","));
    const url = `/api/duty-tax/report/${format}?${params.toString()}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.blob())
      .then(blob => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `warehouse-cost-${tab}.${format === "excel" ? "xlsx" : "pdf"}`;
        a.click();
      })
      .catch(() => toast.error("Gagal export"));
  };

  const updateStatus = async (id, newStatus) => {
    setIsUpdating(true);
    try {
      await axios.patch(`${API_URL}/duty-tax/${id}/status`, {
        status: newStatus,
        notes_finance: approvalNote[id] || null,
        changed_by: getUser().name || "Finance",
      }, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success(`Status → ${STATUS_CONFIG[newStatus]?.label}`);
      setDetailCache(prev => { const n = {...prev}; delete n[id]; return n; });
      setExpandedId(null);
      fetchSubmissions(page, activeTab);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal update status");
    } finally { setIsUpdating(false); }
  };

  const totalPages = Math.ceil(total / limit);
  const TABS = [
    { key: "antrian",       label: "⏳ Antrian" },
    { key: "warehouse_cost",label: "📦 Warehouse Cost" },
    { key: "rekap",         label: "🏭 Rekap Warehouse Cost" },
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="text-blue-600" size={24} /> Duty &amp; Tax
          </h1>
          <p className="text-sm text-gray-500 mt-1">Tagihan dari Tim Clearance</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => navigate("/portal")} className="flex items-center gap-2 px-3 py-2 text-sm bg-white border border-gray-200 rounded-xl hover:bg-gray-50 shadow-sm">
            ← Portal
          </button>
          <button onClick={() => fetchSubmissions(1, activeTab)} className="flex items-center gap-2 px-3 py-2 text-sm bg-white border border-gray-200 rounded-xl hover:bg-gray-50 shadow-sm">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        {TABS.map(tab => (
          <button key={tab.key} onClick={() => handleTabChange(tab.key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.key ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700"}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Warehouse Cost Filter */}
      {activeTab === "warehouse_cost" && (
        <FilterExport
          filters={wcFilters} setFilters={setWcFilters}
          onSearch={() => fetchSubmissions(1, "warehouse_cost")}
          onExport={(fmt) => exportReport(fmt, "warehouse_cost")}
          selectedIds={selectedIds}
          onSelectAll={() => setSelectedIds(submissions.map(s => s.id))}
          onClearAll={() => setSelectedIds([])}
          submissions={submissions}
          extraFilters={
            <select value={wcFilters.type} onChange={e => setWcFilters(p => ({...p, type: e.target.value}))}
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm">
              <option value="import">Import</option>
              <option value="export">Export</option>
            </select>
          }
        />
      )}

      {/* Rekap Filter */}
      {activeTab === "rekap" && (
        <FilterExport
          filters={rekapFilters} setFilters={setRekapFilters}
          onSearch={() => fetchSubmissions(1, "rekap")}
          onExport={(fmt) => exportReport(fmt, "rekap")}
          selectedIds={selectedIds}
          onSelectAll={() => setSelectedIds(submissions.map(s => s.id))}
          onClearAll={() => setSelectedIds([])}
          submissions={submissions}
          extraFilters={<>
            <select value={rekapFilters.type} onChange={e => setRekapFilters(p => ({...p, type: e.target.value}))}
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm">
              <option value="import">Import</option>
              <option value="export">Export</option>
            </select>
            <select value={rekapFilters.gudang} onChange={e => setRekapFilters(p => ({...p, gudang: e.target.value}))}
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm">
              <option value="MSA">MSA</option>
              <option value="GDWD">GDWD</option>
              <option value="JAS">JAS</option>
              <option value="GARUDA">GARUDA</option>
            </select>
          </>}
        />
      )}

      {/* List */}
      <SubmissionList
        submissions={submissions}
        isLoading={isLoading}
        detailCache={detailCache}
        setDetailCache={setDetailCache}
        expandedId={expandedId}
        setExpandedId={setExpandedId}
        onUpdateStatus={updateStatus}
        approvalNote={approvalNote}
        setApprovalNote={setApprovalNote}
        isUpdating={isUpdating}
        showActions={activeTab === "antrian"}
        selectedIds={selectedIds}
        setSelectedIds={setSelectedIds}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          {Array.from({length: totalPages}, (_, i) => i+1).map(p => (
            <button key={p} onClick={() => fetchSubmissions(p, activeTab)}
              className={`w-8 h-8 text-sm rounded-lg ${page === p ? "bg-blue-600 text-white" : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
              {p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default DutyTaxPage;
